import { http } from "@/lib/request";
import type { PageParams, PageResult } from "@/types/api";

export interface OrderListItem {
  id: number;
  sn: string;
  order_status: number;
  order_status_desc: string;
  pay_status: number;
  pay_way?: number;
  pay_way_desc?: string;
  pay_time?: string;
  goods_price?: number;
  discount_amount?: number;
  car_amount?: number;
  order_amount?: number;
  total_amount?: number;
  total_order_amount: number;
  total_gap_amount?: number;
  total_append_amount?: number;
  total_append_duration?: number;
  append_count?: number;
  has_append?: number;
  append_btn?: number;
  estimated_finish_time?: string;
  estimated_arrival_minutes?: number;
  comment_btn: number;
  look_comment_btn: number;
  cancel_btn?: number;
  user_cancel_btn?: number;
  pay_btn?: number;
  create_time?: string;
  appoint_time_desc?: string;
  appoint_date?: string;
  appoint_time?: string;
  user_remark?: string;
  order_remarks?: string;
  order_distance?: string;
  cancel_time?: string;
  server_finish_time?: string;
  address_snap?: Record<string, unknown>;
  coach_info?: {
    id?: number;
    name?: string;
    mobile?: string;
    sn?: string;
    work_photo?: string;
  };
  order_goods: Array<{
    id: number;
    goods_id: number;
    goods_name: string;
    goods_image: string;
    goods_num: number;
    goods_price: number;
    duration: number;
    comment_id?: number | null;
  }>;
  order_append?: OrderAppendItem[];
  pending_append?: OrderAppendItem[];
  pending_append_count?: number;
  pending_append_amount?: number;
  pending_append_pay_btn?: number;
}

export interface OrderAppendItem {
  id: number;
  sn: string;
  goods_id?: number;
  goods_name?: string;
  duration: number;
  goods_num: number;
  total_duration?: number;
  goods_price?: number;
  order_amount: number;
  pay_status?: number;
  pay_time?: string;
  refund_amount?: number;
}

export interface SettlementGoodsItem {
  id: number;
  name?: string;
  image?: string;
  price?: number;
  scribing_price?: number;
  duration?: number;
  marketing_tags?: string[];
  goods_num?: number;
}

export interface SettlementTripWayItem {
  type: number;
  type_desc?: string;
  distance?: number;
  amount?: number;
  original_amount?: number;
  free_travel_fee?: number;
}

export interface SettlementCouponItem {
  id: number;
  cl_id: number;
  money: number;
  condition?: string;
  use_end_time_text?: string;
  tips?: string;
  get_status?: number;
}

export interface OrderSettlementResult {
  total_num: number;
  total_goods_price: number;
  total_amount: number;
  order_amount: number;
  user_remark?: string;
  appoint_time?: number | null;
  address?: Record<string, unknown>;
  goods: SettlementGoodsItem[];
  trip_way_lists: SettlementTripWayItem[];
  trip_way: number;
  coach_id: number;
  coach?: Record<string, unknown>;
  total_duration?: number;
  distance?: number | string;
  distance_desc?: string;
  pay_way_list?: Array<Record<string, unknown>>;
  car_amount?: number;
  original_car_amount?: number;
  free_travel_fee?: number;
  free_travel_fee_amount?: number;
  cl_id?: number;
  discount_amount?: number;
  coupon_lists?: {
    usable: SettlementCouponItem[];
    disabled: SettlementCouponItem[];
  };
}

export interface OrderPayWayItem {
  id: number;
  name: string;
  pay_way: number;
  is_default: number;
  image?: string;
  extra?: string;
}

export interface OrderPayWayResult {
  lists: OrderPayWayItem[];
  order_amount?: number;
  cancel_time?: number;
}

export interface OrderPrepayResult {
  order_id: number;
  pay_way: number;
  config?: Record<string, unknown>;
}

export interface OrderSubmitResult {
  type: string;
  order_id: number;
  sn?: string;
}

export interface OrderPayResult {
  id: number;
  sn?: string;
  pay_way: number;
  pay_status: number;
  pay_way_desc?: string;
  order_amount?: number;
  total_amount?: number | string;
  pay_time?: string;
  order_status?: number;
}

export interface OrderAppendSettlementResult {
  order_id: number;
  goods_id: number;
  goods_name?: string;
  goods_image?: string;
  goods_num: number;
  duration: number;
  total_duration: number;
  goods_price: number;
  order_amount: number;
  max_sessions: number;
  paid_append_duration?: number;
  estimated_finish_time?: string;
}

export interface OrderAppendSubmitResult {
  id: number;
  order_id: number;
  sn: string;
  type: string;
  order_amount: number;
  total_duration?: number;
  estimated_finish_time?: string;
}

export interface OrderMockPayNotifyResult {
  order_id: number;
  result: string;
  message?: string;
  pay_status: number;
}

export interface CoachServiceTimeSlot {
  time: string;
  status: number;
  timestamp: number;
}

export interface CoachServiceTimeDay {
  time_date: string;
  time_tips: string;
  time_lists: CoachServiceTimeSlot[];
}

export const orderApi = {
  getOrderList(params: PageParams & { order_status?: number }) {
    return http.get<PageResult<OrderListItem>>("/order/lists", { params });
  },

  getOrderDetail(id: number | string) {
    return http.get<OrderListItem & Record<string, unknown>>("/order/detail", {
      params: { id },
    });
  },

  getCoachServerTime(params: { coach_id: number; goods_id: number; goods_num?: number }) {
    return http.get<CoachServiceTimeDay[]>("/order/getCoachServerTime", {
      params,
    });
  },

  getOrderSettlement(payload: {
    coach_id: number;
    address_id?: number;
    longitude?: number;
    latitude?: number;
    trip_way?: number;
    cl_id?: number;
    goods: Array<{ id: number; goods_num: number }>;
  }) {
    return http.post<OrderSettlementResult>("/order/placeOrder", {
      action: "settlement",
      ...payload,
    });
  },

  submitOrder(payload: {
    coach_id: number;
    address_id: number;
    trip_way?: number;
    cl_id?: number;
    terminal?: string;
    user_remark?: string;
    appoint_time: number;
    goods: Array<{ id: number; goods_num: number }>;
  }) {
    return http.post<OrderSubmitResult>("/order/placeOrder", {
      action: "submit",
      terminal: "h5",
      ...payload,
    });
  },

  getPayWays(orderId: number) {
    return http.get<OrderPayWayResult>("/order/payWay", {
      params: { from: "order", order_id: orderId },
    });
  },

  settleOrderAppend(orderId: number, goodsId: number, goodsNum: number) {
    return http.post<OrderAppendSettlementResult>("/order/orderAppend", {
      action: "settlement",
      order_id: orderId,
      goods: [{ id: goodsId, goods_num: goodsNum }],
    });
  },

  submitOrderAppend(orderId: number, goodsId: number, goodsNum: number) {
    return http.post<OrderAppendSubmitResult>("/order/orderAppend", {
      action: "submit",
      order_id: orderId,
      goods: [{ id: goodsId, goods_num: goodsNum }],
    });
  },

  cancelOrder(orderId: number) {
    return http.post<void>("/order/cancel", { id: orderId });
  },

  deleteOrder(orderId: number) {
    return http.post<void>("/order/del", { id: orderId });
  },

  finishOrder(orderId: number) {
    return http.post<void>("/order/finish", { id: orderId });
  },

  prepayOrder(orderId: number, payWay: number) {
    return http.post<OrderPrepayResult>("/pay/prepay", {
      from: "order",
      order_id: orderId,
      pay_way: payWay,
    });
  },

  prepayOrderAppend(appendId: number, payWay: number) {
    return http.post<OrderPrepayResult>("/pay/prepay", {
      from: "orderAppend",
      order_id: appendId,
      pay_way: payWay,
    });
  },

  mockPayNotify(orderId: number, result: "success" | "fail" | "cancel", payWay: number, from = "order") {
    return http.post<OrderMockPayNotifyResult>("/pay/mock/notify", {
      order_id: orderId,
      result,
      pay_way: payWay,
      from,
    });
  },

  getPayResult(orderId: number, from = "order") {
    return http.get<OrderPayResult>("/pay/getPayResult", {
      params: { from, order_id: orderId },
    });
  },
};
